/**
 * WorkerPool calibration sampler coordinator.
 *
 * The sampler owns CPU/PSS measurement math. This coordinator owns the pool-side
 * lifecycle wiring around it: live cadence toggles, start/stop, stats exposure,
 * and per-call begin/end handoff from {@link ./worker-pool.js WorkerPool}.
 *
 * @module worker-pool/resource-coordinator
 */

import type { BrowserLogger } from '../logger.js';
import type { PooledChild } from './bookkeeping.js';
import {
  type CallSample,
  type ProcStat,
  WorkerPoolResourceSampler,
  type WorkerPoolResourceStats,
} from './resource-sampler.js';

export interface WorkerPoolResourceCoordinatorOptions<Handle> {
  readonly children: Set<PooledChild<Handle>>;
  readonly measureResourcesMs?: number;
  readonly now: () => number;
  readonly isDraining: () => boolean;
  readonly logger: BrowserLogger;
  readonly readProcStat?: (pid: number) => Promise<ProcStat | undefined>;
  readonly readPssBytes?: (pid: number) => Promise<number | undefined>;
  readonly listProcPids?: () => Promise<number[]>;
}

/**
 * Pool-side owner for the optional calibration sampler.
 *
 * Keeping this separate lets {@link WorkerPoolResourceSampler} stay focused on
 * measurement and keeps `WorkerPool` focused on scheduling/spawn/retire state.
 */
export class WorkerPoolResourceCoordinator<Handle> {
  private readonly children: Set<PooledChild<Handle>>;
  private readonly now: () => number;
  private readonly isDraining: () => boolean;
  private readonly logger: BrowserLogger;
  private readonly readers: Pick<
    WorkerPoolResourceCoordinatorOptions<Handle>,
    'readProcStat' | 'readPssBytes' | 'listProcPids'
  >;

  private sampler: WorkerPoolResourceSampler<Handle> | null = null;
  /** The sampler's current poll cadence (ms); 0 means off. */
  private measureResourcesMs = 0;

  constructor(options: WorkerPoolResourceCoordinatorOptions<Handle>) {
    this.children = options.children;
    this.now = options.now;
    this.isDraining = options.isDraining;
    this.logger = options.logger;
    this.readers = {
      ...(options.readProcStat ? { readProcStat: options.readProcStat } : {}),
      ...(options.readPssBytes ? { readPssBytes: options.readPssBytes } : {}),
      ...(options.listProcPids ? { listProcPids: options.listProcPids } : {}),
    };
    if (options.measureResourcesMs) this.setMeasureResourcesMs(options.measureResourcesMs);
  }

  /**
   * Turn the per-driver calibration sampler on/off (or change cadence) live.
   * `0` disables measurement and drops accumulated samples; a positive cadence
   * creates a fresh sampler and starts it immediately when the pool is already
   * live.
   */
  setMeasureResourcesMs(ms: number): void {
    const next = ms > 0 ? ms : 0;
    if (next === this.measureResourcesMs) return;
    this.measureResourcesMs = next;
    this.sampler?.stop();
    if (next === 0) {
      this.sampler = null;
      return;
    }
    this.sampler = new WorkerPoolResourceSampler({
      children: this.children,
      intervalMs: next,
      now: this.now,
      isDraining: this.isDraining,
      logger: this.logger,
      ...this.readers,
    });
    if (this.children.size > 0 && !this.isDraining()) this.sampler.start();
  }

  start(): void {
    this.sampler?.start();
  }

  stop(): void {
    this.sampler?.stop();
  }

  stats(): WorkerPoolResourceStats | undefined {
    return this.sampler?.stats();
  }

  /**
   * Snapshot a per-call child's CPU/clock before dispatch. Returns undefined
   * whenever measurement is off, the child has no pid, or `/proc` is unreadable.
   */
  beginCall(pid: number | undefined): Promise<CallSample | undefined> {
    if (this.sampler === null || pid === undefined) return Promise.resolve(undefined);
    return this.sampler.beginCall(pid).catch(() => undefined);
  }

  /**
   * Record a healthy per-call settle. Retired children deliberately skip this:
   * their final `/proc` read would usually fail after SIGKILL, and partial samples
   * are more misleading than absent calibration data.
   */
  async endCall(pid: number | undefined, sample: CallSample | undefined): Promise<void> {
    if (this.sampler === null || pid === undefined || sample === undefined) return;
    await this.sampler.endCall(pid, sample).catch(() => {});
  }
}
